import React from "react";
import { useTranslation } from "react-i18next";
import { differenceInDays } from "date-fns";
import { isEmpty, map } from "lodash";
import Text from "@/components/ui/text";
import KPIsContainer from "@/components/controls/KPIsContainer";
import ScoreBars, { ScoreStrength } from "@/components/controls/ScoreBars";
import SingleSelect from "@/components/controls/SingleSelect";
import {
  PersonData,
  PersonReferenceabilityLevel
} from "@/entities/people/types";
import {
  referenceabilityLevels,
  referenceabilityLevelsToScoreBarsMap
} from "@/entities/people/helpers";
import { getEnumTKey } from "@/utils/strings";
import IcpFitValue from "@/components/controls/DetailedView/ICPFit/IcpFitValue";
import { IcpFitSubject } from "@/entities/icpFit/types";
import IcpFitDetailsPopover from "@/components/controls/DetailedView/ICPFit/IcpFitDetailsPopover";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";

interface Props {
  person?: PersonData;
  containerClassName?: string;
  isQuickView?: boolean;
  onUpdateField?: (field: string, newValue: string | number) => void;
}

const EMPTY_VALUE = "-";

const PersonKPIs: React.FC<Props> = ({
  person,
  containerClassName,
  isQuickView,
  onUpdateField
}) => {
  const { t } = useTranslation();
  const { enabled: isIcpFitEnabled } = useFeatureFlag(
    FeatureFlags.CRM_5676_ICP
  );

  if (!person || !isIcpFitEnabled) return null;

  const renderSinceLastActivity = (value: string | null | undefined) => {
    if (isEmpty(value)) {
      return (
        <Text variant={isQuickView ? "lgLabel" : "lgSubtitleH1"}>
          {EMPTY_VALUE}
        </Text>
      );
    }
    const days = differenceInDays(new Date(), value as string);
    return (
      <Text variant={isQuickView ? "lgLabel" : "lgSubtitleH1"}>
        {`${days} ${t(days === 1 ? "dateTime.day" : "dateTime.days")}`}
      </Text>
    );
  };

  const kpis: { key: string; subtitle: string; render: () => JSX.Element }[] = [
    {
      key: "icpFit",
      subtitle: t("people.kpis.personIcpFit"),
      render: () => (
        <div className="flex items-center gap-1 min-w-0">
          <IcpFitValue
            value={person.icpFit}
            isQuickView={isQuickView}
            subject={IcpFitSubject.PERSON}
            emptyVariant={isQuickView ? "lgLabel" : "lgSubtitleH1"}
          />
          <IcpFitDetailsPopover
            icpFit={person.icpFit}
            bullets={person.icpMetadata?.bullets}
            previewLabel={t("people.kpis.personIcpFit")}
          />
        </div>
      )
    },
    {
      key: "lastActivity",
      subtitle: t("people.kpis.sinceLastActivity"),
      render: () => renderSinceLastActivity(person.lastActivity)
    },
    {
      key: "referenceability",
      subtitle: t("people.kpis.referenceability"),
      render: () => (
        <SingleSelect
          fieldName="referenceability"
          variant="inline"
          options={map(referenceabilityLevels, (level) => ({
            id: level,
            name: PersonReferenceabilityLevel[level]
          }))}
          formatter={(value) =>
            t(
              getEnumTKey(
                "people",
                "refLevels",
                PersonReferenceabilityLevel,
                value as number
              )
            )
          }
          selectedValue={
            person.referenceability ?? PersonReferenceabilityLevel.UNKNOWN
          }
          defaultValue={PersonReferenceabilityLevel.UNKNOWN}
          renderIcon={(value) => (
            <ScoreBars
              score={
                referenceabilityLevelsToScoreBarsMap[
                  Number(value)
                ] as ScoreStrength
              }
            />
          )}
          onSelection={(newValue) =>
            onUpdateField?.("referenceability", newValue)
          }
          triggerVariant={isQuickView ? "lgLabel" : "smSubtitle"}
          popoverClassName="!max-w-fit"
        />
      )
    }
  ];

  return (
    <KPIsContainer className={containerClassName}>
      {map(kpis, ({ key, subtitle, render }) => (
        <div key={key} className="flex flex-col gap-1 p-3 min-w-0">
          <div className="flex items-center h-12">{render()}</div>
          <Text variant="bodySecondary">{subtitle}</Text>
        </div>
      ))}
    </KPIsContainer>
  );
};

export default PersonKPIs;
